// The Guv POP Tag in-app scan, as a hook (Session APF). "POP" is Proof Of Presence.
//
// WHY THIS IS A HOOK. Until Session APF the whole scan lived inside one screen, PopTagScanPage.tsx
// (Session AND), and the job sheet never mentioned the tag at all. The Director then asked for the
// tag to become an ON/OFF PAIR inside the job itself: tap it as you arrive, tap it again as you
// leave. That gave the scan a second caller (PopTagStep.tsx, rendered twice on the job sheet), and a
// scan is not something to implement twice. Every rule below was learned the hard way on the first
// caller: the live identity preflight, the controller created BEFORE the preflight, the listener
// that does not trust the browser to stay quiet after an abort, the thirty second timeout, the
// keep-listening behaviour for the wrong sticker. A second copy would have been free to drift from
// any of them, and the ones that matter most here fail silently. So the state machine was lifted out
// WHOLE, comments and all, and both callers are thin consumers of it.
//
// WHAT A CALLER GETS. The phase the scan is in, the message to show, whether the preflight could
// not be confirmed, the server's answer and what it means (popTagOutcome), and three controls:
// scan, cancel and reset. Session APF added two things for the job sheet, neither of which the
// standalone screen reads: `failure`, which says WHY a recording failed (so the sheet can tell "no
// signal" from "that tag is not in use"), and `readAt`, the phone's own time at the moment the
// sticker was read (so the sheet still has the moment of the tap when the recording never reached
// the server).
//
// WHAT A CALLER MAY SEND. `options.body` is asked for at the moment of the POST and may name a
// direction ("arrival" or "departure") and the job the tap was made from. The standalone screen
// passes nothing, and then NO body is sent at all, not an empty object: that screen records exactly
// the undirected tap it always has. See api.publicTap.record.
//
// HOW THE IDENTITY GUARANTEE IS DELIVERED is set out at the head of PopTagScanPage.tsx, where it
// has always been, and is not repeated here. The half of it that lives in this file is PREVENTION:
// the api.me() round trip below. The other half, DETECTION, is the caller reading `outcome`.
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type PublicTapResult } from "../../lib/api";
import {
  DEFAULT_POP_TAG_ACTION_LABEL,
  canRecordPopTagIdentity,
  isPopTagScanSupported,
  popTagFromRecords,
  popTagOutcome,
  popTagScanErrorMessage,
  popTagScanMessage,
  type PopTagOutcome,
} from "../../lib/popTag";

// How long to wait for a tag before giving up and saying so.
//
// A TIMEOUT IS NOT OPTIONAL HERE, and the reason is a silent failure rather than a slow one. Chrome
// SUSPENDS a scan while the document is hidden: no reading event, no readingerror, no rejection, and
// the reader stays activated. An engineer who presses Scan and then lets the screen dim gets a
// spinner that waits for ever with nothing wrong that any error path can see. Thirty seconds is long
// enough to find the sticker on a log card and short enough that the screen admits defeat before the
// engineer does.
const SCAN_TIMEOUT_MS = 30_000;

export type PopTagScanPhase =
  | "unsupported" // no NDEFReader: not Android Chrome, or not a secure context
  | "idle"
  | "checking" // the api.me() preflight is in flight
  | "blocked" // the preflight gave a definitive answer: no usable identity
  | "scanning"
  | "posting"
  | "done"
  | "failed";

// Why a recording failed, set alongside the message whenever the POST itself is refused or lost.
//
//   not_in_use    404. The route's deliberate single answer for a malformed, unknown or
//                 office-cleared token.
//   rate_limited  429.
//   network       anything else: no signal, a dropped connection, a server fault. The tag WAS read
//                 (nothing reaches the POST otherwise), so `readAt` holds the moment of the tap.
export type PopTagScanFailure = "not_in_use" | "rate_limited" | "network";

// What a caller may say about the tap it is recording. Both are optional on the route as well.
export interface PopTagScanBody {
  direction?: "arrival" | "departure";
  jobId?: string;
}

export interface UsePopTagScanOptions {
  // Asked for at the moment of the POST, not at the moment of the press, so it always describes the
  // caller as it is when the tap is actually recorded. Returning undefined sends no body at all.
  body?: () => PopTagScanBody | undefined;
  // Session APF: what to call the control that tries again, in every failure message this hook
  // composes (its own inline copy below, and popTagScanErrorMessage's). Lower case, as it reads mid
  // sentence: "then {actionLabel} again." Defaults to "press Scan", the standalone Scan POP Tag
  // screen's own button, so that screen's copy is unchanged. The job sheet's in-flow step
  // (PopTagStep.tsx) sets it to "tap the tag", its own button's name.
  actionLabel?: string;
  // Session APG: hand the token to the caller instead of recording it. The Home screen's scan button
  // does not know which job (or which end of it) a tap belongs to until the server has said which
  // lift the tag is on and which of the engineer's jobs are open there, so for that caller "the tag
  // was read" is the START of the work, not the moment to post. When this is given the hook does the
  // whole read exactly as before (preflight, reader, timeout, wrong-sticker messages), then stops at
  // "idle" and calls this with the token; the caller finishes the scan by calling `recordNow`. Never
  // given by the job sheet's steps or the standalone screen, which record at once, as they always did.
  onRead?: (token: string) => void;
}

export interface PopTagScan {
  // True when this browser can read a tag at all. Fixed for the life of the component.
  supported: boolean;
  phase: PopTagScanPhase;
  message: string | null;
  // Set when the preflight could not reach the server at all. The scan still runs (see `scan`), so
  // this is a warning the engineer can read, never a refusal.
  unconfirmed: boolean;
  result: PublicTapResult | null;
  outcome: PopTagOutcome | null;
  failure: PopTagScanFailure | null;
  // The phone's own ISO time at which the tag was successfully READ, set just before the POST. The
  // server's `result.tappedAt` is the better time whenever there is one; this is for when there is not.
  readAt: string | null;
  scan: () => Promise<void>;
  cancel: () => void;
  reset: () => void;
  // Session APG: post a tap for a token the caller was handed by `onRead`, naming the direction and
  // job it has since worked out. The same call the hook makes for itself after a read, so the answer
  // (result, outcome, failure, message, phase) lands in the same state and is read the same way.
  recordNow: (token: string, body?: PopTagScanBody) => Promise<void>;
}

export function usePopTagScan(options?: UsePopTagScanOptions): PopTagScan {
  const [supported] = useState(() => isPopTagScanSupported());
  const [phase, setPhase] = useState<PopTagScanPhase>(() => (isPopTagScanSupported() ? "idle" : "unsupported"));
  const [message, setMessage] = useState<string | null>(null);
  // Set when the preflight could not reach the server at all. The scan still runs (see the handler),
  // so this is a warning the engineer can read, never a refusal.
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [result, setResult] = useState<PublicTapResult | null>(null);
  const [outcome, setOutcome] = useState<PopTagOutcome | null>(null);
  const [failure, setFailure] = useState<PopTagScanFailure | null>(null);
  const [readAt, setReadAt] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a second `reading` event arriving while the first POST is still in flight, which
  // a phone resting on a tag will produce.
  const handledRef = useRef(false);

  // The caller's body function, kept in a ref and refreshed after every commit. The POST happens a
  // preflight and a tag read after the press that started it, and the callbacks below are created
  // once, so reading the option through a ref is what lets them see the caller as it is NOW without
  // being torn down and rebuilt on every render of a job sheet that re-renders on every keystroke.
  const bodyRef = useRef(options?.body);
  useEffect(() => {
    bodyRef.current = options?.body;
  });
  // Session APF: the control this hook names in its failure copy, kept the same way and for the same
  // reason as bodyRef above -- read by callbacks that are created once and must still see the
  // caller's choice without being rebuilt on every render.
  const actionLabelRef = useRef(options?.actionLabel ?? DEFAULT_POP_TAG_ACTION_LABEL);
  useEffect(() => {
    actionLabelRef.current = options?.actionLabel ?? DEFAULT_POP_TAG_ACTION_LABEL;
  });
  // Session APG: the caller's read handler, kept the same way and for the same reason -- it is read
  // from the reading listener, which is created once per scan and outlives the render that made it.
  const onReadRef = useRef(options?.onRead);
  useEffect(() => {
    onReadRef.current = options?.onRead;
  });

  const stopScan = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // NOT cleared to null afterwards. The controller has to stay reachable so a continuation still
    // suspended on an await can see `signal.aborted` and refuse to go on; nulling it here would hand
    // that continuation a fresh-looking ref and let it start the scan anyway.
    abortRef.current?.abort();
  }, []);

  // Abort on unmount, or the reader outlives the route change and keeps the NFC radio active.
  //
  // THIS ONLY WORKS BECAUSE THE CONTROLLER IS CREATED BEFORE THE PREFLIGHT, and the first version of
  // this code got that wrong in a way worth recording. The controller used to be created after
  // `await api.me()`, so during the whole "checking" phase this cleanup aborted nothing: an engineer
  // who pressed Scan and then tapped Home while the preflight was in flight left a continuation that
  // went on to construct a reader and start scanning on an unmounted component. The orphaned
  // listener would then record a real attendance row for whatever tag the phone next passed, with no
  // screen anywhere showing it had happened. Every await below is followed by an aborted check for
  // the same reason.
  useEffect(() => stopScan, [stopScan]);

  const cancel = useCallback(() => {
    stopScan();
    handledRef.current = true;
    setPhase("idle");
    setMessage(null);
    setUnconfirmed(false);
  }, [stopScan]);

  // Back to the start, with the last answer put away. The standalone screen calls this from "Scan
  // another tag"; nothing is running at that point, so there is nothing to stop.
  const reset = useCallback(() => {
    setPhase("idle");
    setResult(null);
    setOutcome(null);
    setMessage(null);
    setUnconfirmed(false);
    setFailure(null);
    setReadAt(null);
  }, []);

  const record = useCallback(async (token: string, explicitBody?: PopTagScanBody) => {
    setPhase("posting");
    try {
      // NO SECOND ARGUMENT AT ALL when the caller has nothing to say, rather than an undefined one.
      // The standalone screen's request is then the very request it made before this hook existed:
      // a POST with no body. (Session APG: a caller that finished a scan itself, through recordNow,
      // names its body; every other caller still has it asked for here, at the moment of the POST.)
      const body = explicitBody ?? bodyRef.current?.();
      const res = body ? await api.publicTap.record(token, body) : await api.publicTap.record(token);
      setResult(res);
      setOutcome(popTagOutcome(res));
      setPhase("done");
    } catch (err) {
      // 404 is the route's deliberate single answer for a malformed, unknown or office-cleared
      // token, and it must stay one answer here too: telling the engineer which of the three it was
      // would hand anyone holding the page an oracle over which lifts carry a tag.
      const status = err instanceof ApiError ? err.status : 0;
      setFailure(status === 404 ? "not_in_use" : status === 429 ? "rate_limited" : "network");
      setMessage(
        status === 404
          ? "That tag is not in use. Ask the office to check it."
          : status === 429
            ? `Too many scans in the last minute. Wait a moment, then ${actionLabelRef.current} again.`
            : `The visit could not be recorded. Check your signal and ${actionLabelRef.current} again.`,
      );
      setPhase("failed");
    }
  }, []);

  const scan = useCallback(async () => {
    // Nothing to do on a phone that cannot read a tag. Neither caller offers a button there, so this
    // is a guard for a future one rather than a path either of them reaches.
    if (!supported) return;

    setMessage(null);
    setUnconfirmed(false);
    setResult(null);
    setOutcome(null);
    setFailure(null);
    setReadAt(null);
    handledRef.current = false;

    // The controller is created HERE, before the preflight, not after it. It is what the unmount
    // cleanup and the Cancel button abort, and until it exists they abort nothing. See the comment
    // on the cleanup effect for what that cost when it was the other way round.
    const controller = new AbortController();
    abortRef.current = controller;

    // ---- Prevention: confirm the session with the server, not with the client ----
    //
    // NEVER read the cached user from the auth context for this. src/lib/auth.tsx hydrates from
    // localStorage and deliberately KEEPS the last-known user through any non-401 failure, so the
    // context can hold a ghost with no live cookie behind it. Trusting it would ship the exact bug
    // the in-app scan exists to close, with a green test suite.
    setPhase("checking");
    try {
      const me = await api.me();
      if (!canRecordPopTagIdentity(me.user)) {
        // A 200 is not enough on its own. An in-window auditor is allowed through /api/auth/me by
        // AUDITOR_SESSION_PATHS, and the tap route will refuse them as the identity, so accepting a
        // bare 200 here would wave through the one role guaranteed to record anonymously.
        setMessage(
          `This account cannot record a POP Tag visit. Sign in with your engineer account, then ${actionLabelRef.current} again.`,
        );
        setPhase("blocked");
        return;
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Definitive: the cookie the POST would have carried is not valid. Refuse before asking the
        // engineer to hold their phone against a tag for a record that would be nameless.
        setMessage(`You are not signed in on this phone. Sign in, then ${actionLabelRef.current} again.`);
        setPhase("blocked");
        return;
      }
      // ANYTHING ELSE IS NOT DEFINITIVE, AND THE SCAN GOES AHEAD. A blocked scan records nothing at
      // all, and refusing on a dropped packet would destroy a visit that would have recorded
      // perfectly well. The detection half is certain on a fresh tap, so allowing the scan risks an
      // honest warning and never a silent misreport, which is the trade this whole feature is built
      // on.
      setUnconfirmed(true);
    }

    // The engineer left, or pressed Cancel, while the preflight was in flight. Stop here: starting a
    // reader now would leave it scanning with nothing on screen, and its listener would record a
    // real attendance row nobody could see.
    if (controller.signal.aborted) return;

    // ---- The read ----
    let reader: NDEFReader;
    try {
      reader = new NDEFReader();
    } catch {
      setMessage(popTagScanErrorMessage(undefined, actionLabelRef.current));
      setPhase("failed");
      return;
    }

    reader.addEventListener("reading", (event: NDEFReadingEvent) => {
      // `aborted` as well as handledRef: a reader whose scan was aborted should deliver nothing, but
      // this listener is the last thing standing between a stray event and a written attendance row,
      // so it does not rely on the browser to be well behaved about it.
      if (handledRef.current || controller.signal.aborted) return;
      const read = popTagFromRecords(event.message?.records);
      if (read.kind !== "pop") {
        // Not our tag. Keep listening rather than tearing the scan down: the engineer is most likely
        // to have brushed the customer sticker on the way to the right one, and making them press
        // Scan again for that is a needless second gesture in a motor room.
        setMessage(popTagScanMessage(read.kind));
        return;
      }
      handledRef.current = true;
      stopScan();
      setMessage(null);
      // The moment of the tap, by this phone's clock, taken BEFORE the POST is attempted. If the
      // recording never reaches the server this is the only time anybody has for the tap, and one
      // taken after a request had timed out would be late by however long the timeout ran.
      setReadAt(new Date().toISOString());
      // Session APG: a caller that works out what the tap is for AFTER the read (the Home scan
      // button) takes the token here and posts it itself, through recordNow. The scan is over either
      // way: the reader has been stopped above, so "idle" is the honest phase to leave it in.
      if (onReadRef.current) {
        setPhase("idle");
        onReadRef.current(read.token);
        return;
      }
      void record(read.token);
    });

    reader.addEventListener("readingerror", () => {
      if (handledRef.current || controller.signal.aborted) return;
      // The event carries no error object and no detail of any kind, so this is as specific as it is
      // honest to be.
      setMessage("Could not read that tag. Hold the phone still against it and try again.");
    });

    try {
      setPhase("scanning");
      await reader.scan({ signal: controller.signal });
      // Aborted while scan() was settling: do not arm a timer that would fire against a screen the
      // engineer has already left.
      if (controller.signal.aborted) return;
      timerRef.current = setTimeout(() => {
        if (handledRef.current || controller.signal.aborted) return;
        handledRef.current = true;
        stopScan();
        setMessage(`No tag detected. Keep the screen on, hold the phone against the sticker, then ${actionLabelRef.current} again.`);
        setPhase("failed");
      }, SCAN_TIMEOUT_MS);
    } catch (err) {
      const name = err instanceof DOMException ? err.name : undefined;
      // Our own cancel or timeout firing, which is control flow and not a fault. Surfacing it would
      // put a red banner in front of the engineer every time they backed out of a scan.
      if (name === "AbortError") return;
      setMessage(popTagScanErrorMessage(name, actionLabelRef.current));
      setPhase("failed");
    }
  }, [record, stopScan, supported]);

  return { supported, phase, message, unconfirmed, result, outcome, failure, readAt, scan, cancel, reset, recordNow: record };
}

// The recorded instant as London wall clock. A UTC render would read an hour early all summer, on
// the one number a tap exists to show. Same treatment as the sticker page. It lives here so that
// both callers of the hook show a tap's time the same way.
export function londonTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
}
